import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import './RecoveryConfiguration.css';

interface TierConfig {
  name: string;
  offerType: 'percentage' | 'dollar' | 'free_item' | 'gift_card' | 'full_comp' | 'custom';
  offerValue: number;
  maxOfferCap: number;
  expirationDays: number;
  slaHours: number;
  autoApprove: boolean;
  requireManagerCall: boolean;
  deliveryMethod: 'sms' | 'email' | 'both';
  redemptionTracking: boolean;
}

interface EscalationContact {
  name: string;
  phone: string;
  email: string;
}

interface RecoveryConfig {
  storeId: string;
  tiers: { [key: number]: TierConfig };
  brandVoice: { tone: string; style: string; signOff: string };
  escalationContacts: { manager: EscalationContact; owner: EscalationContact };
  menuPool: string[];
  restaurantName: string;
}

interface Props {
  storeId: string;
  storeName: string;
}

const DEFAULT_TIERS: { [key: number]: TierConfig } = {
  1: { name: 'Minor', offerType: 'free_item', offerValue: 0, maxOfferCap: 15, expirationDays: 30, slaHours: 24, autoApprove: false, requireManagerCall: false, deliveryMethod: 'email', redemptionTracking: true },
  2: { name: 'Moderate', offerType: 'percentage', offerValue: 20, maxOfferCap: 25, expirationDays: 30, slaHours: 12, autoApprove: false, requireManagerCall: false, deliveryMethod: 'both', redemptionTracking: true },
  3: { name: 'Severe', offerType: 'dollar', offerValue: 25, maxOfferCap: 50, expirationDays: 45, slaHours: 4, autoApprove: false, requireManagerCall: false, deliveryMethod: 'both', redemptionTracking: true },
  4: { name: 'Safety', offerType: 'full_comp', offerValue: 0, maxOfferCap: 100, expirationDays: 60, slaHours: 1, autoApprove: false, requireManagerCall: true, deliveryMethod: 'both', redemptionTracking: true }
};

export const RecoveryConfiguration: React.FC<Props> = ({ storeId, storeName }) => {
  const [config, setConfig] = useState<RecoveryConfig>({
    storeId,
    tiers: DEFAULT_TIERS,
    brandVoice: { tone: 'warm and professional', style: 'conversational yet respectful', signOff: 'the management team' },
    escalationContacts: { manager: { name: '', phone: '', email: '' }, owner: { name: '', phone: '', email: '' } },
    menuPool: [],
    restaurantName: storeName
  });
  const [expandedTier, setExpandedTier] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, [storeId]);

  const fetchConfig = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.recoveryConfig(storeId));
      if (response.ok) {
        const data = await response.json();
        if (data.config) {
          setConfig(prev => ({
            ...prev,
            ...data.config,
            tiers: { ...DEFAULT_TIERS, ...(data.config.tiers || {}) },
            brandVoice: { ...prev.brandVoice, ...(data.config.brandVoice || {}) },
            escalationContacts: {
              manager: { ...prev.escalationContacts.manager, ...(data.config.escalationContacts?.manager || {}) },
              owner: { ...prev.escalationContacts.owner, ...(data.config.escalationContacts?.owner || {}) },
            },
          }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch recovery config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.recoveryConfig(storeId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      if (response.ok) {
        setMessage('Configuration saved successfully');
      } else {
        setMessage('Failed to save configuration');
      }
    } catch (err) {
      setMessage('Error saving configuration');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const updateTier = (tier: number, field: string, value: any) => {
    setConfig(prev => ({
      ...prev,
      tiers: {
        ...prev.tiers,
        [tier]: { ...prev.tiers[tier], [field]: value }
      }
    }));
  };

  const updateContact = (role: 'manager' | 'owner', field: string, value: string) => {
    setConfig(prev => ({
      ...prev,
      escalationContacts: {
        ...prev.escalationContacts,
        [role]: { ...prev.escalationContacts[role], [field]: value }
      }
    }));
  };

  if (loading) return <div className="recovery-config-loading">Loading configuration...</div>;

  const tierColors: { [key: number]: string } = { 1: '#3b82f6', 2: '#f59e0b', 3: '#ef4444', 4: '#7c3aed' };

  return (
    <div className="recovery-configuration">
      <div className="config-header">
        <h2>Guest Recovery Configuration</h2>
        <p>Configure how negative reviews are handled for {storeName}</p>
      </div>

      {message && <div className={`config-message ${message.includes('success') ? 'success' : 'error'}`}>{message}</div>}

      <div className="config-section">
        <h3>Recovery Tiers</h3>
        <p className="section-desc">Define offer levels and SLA windows for each severity tier</p>

        {[1, 2, 3, 4].map(tier => (
          <div key={tier} className="tier-section" style={{ borderLeftColor: tierColors[tier] }}>
            <div className="tier-header" onClick={() => setExpandedTier(expandedTier === tier ? null : tier)}>
              <span className="tier-badge" style={{ backgroundColor: tierColors[tier] }}>Tier {tier}</span>
              <span className="tier-name">{config.tiers[tier]?.name || DEFAULT_TIERS[tier].name}</span>
              <span className="tier-summary">
                {config.tiers[tier]?.offerType === 'percentage' ? `${config.tiers[tier]?.offerValue}%` : `$${config.tiers[tier]?.offerValue}`} off | {config.tiers[tier]?.slaHours}h SLA
              </span>
              <i className={`fas fa-chevron-${expandedTier === tier ? 'up' : 'down'}`}></i>
            </div>

            {expandedTier === tier && (
              <div className="tier-body">
                <div className="tier-grid">
                  <div className="form-group">
                    <label>Offer Type</label>
                    <select value={config.tiers[tier]?.offerType || 'percentage'} onChange={e => updateTier(tier, 'offerType', e.target.value)}>
                      <option value="free_item">Free Item</option>
                      <option value="percentage">Discount %</option>
                      <option value="dollar">Dollar Amount Off</option>
                      <option value="full_comp">Full Comp</option>
                      <option value="gift_card">Gift Card</option>
                      <option value="custom">Custom Text</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Offer Value</label>
                    <input type="number" value={config.tiers[tier]?.offerValue || 0} onChange={e => updateTier(tier, 'offerValue', parseInt(e.target.value) || 0)} />
                  </div>
                  <div className="form-group">
                    <label>Max Cap ($)</label>
                    <input type="number" value={config.tiers[tier]?.maxOfferCap || 0} onChange={e => updateTier(tier, 'maxOfferCap', parseInt(e.target.value) || 0)} />
                  </div>
                  <div className="form-group">
                    <label>Expiration (days)</label>
                    <input type="number" value={config.tiers[tier]?.expirationDays || 30} onChange={e => updateTier(tier, 'expirationDays', parseInt(e.target.value) || 30)} />
                  </div>
                  <div className="form-group">
                    <label>SLA Window (hours)</label>
                    <input type="number" value={config.tiers[tier]?.slaHours || 24} onChange={e => updateTier(tier, 'slaHours', parseInt(e.target.value) || 24)} />
                  </div>
                  <div className="form-group">
                    <label>Delivery Method</label>
                    <select value={config.tiers[tier]?.deliveryMethod || 'email'} onChange={e => updateTier(tier, 'deliveryMethod', e.target.value)}>
                      <option value="email">Email Only</option>
                      <option value="sms">SMS Only</option>
                      <option value="both">Email + SMS</option>
                    </select>
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={config.tiers[tier]?.autoApprove || false} onChange={e => updateTier(tier, 'autoApprove', e.target.checked)} />
                      Auto-approve AI responses
                    </label>
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={config.tiers[tier]?.requireManagerCall || false} onChange={e => updateTier(tier, 'requireManagerCall', e.target.checked)} />
                      Require manager call/follow-up
                    </label>
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={config.tiers[tier]?.redemptionTracking !== false} onChange={e => updateTier(tier, 'redemptionTracking', e.target.checked)} />
                      Track code redemption
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="config-section">
        <h3>Brand Voice</h3>
        <div className="config-grid">
          <div className="form-group">
            <label>Tone</label>
            <input type="text" value={config.brandVoice.tone} onChange={e => setConfig(prev => ({ ...prev, brandVoice: { ...prev.brandVoice, tone: e.target.value } }))} placeholder="e.g., warm and professional" />
          </div>
          <div className="form-group">
            <label>Style</label>
            <input type="text" value={config.brandVoice.style} onChange={e => setConfig(prev => ({ ...prev, brandVoice: { ...prev.brandVoice, style: e.target.value } }))} placeholder="e.g., conversational yet respectful" />
          </div>
          <div className="form-group">
            <label>Sign-off Name</label>
            <input type="text" value={config.brandVoice.signOff} onChange={e => setConfig(prev => ({ ...prev, brandVoice: { ...prev.brandVoice, signOff: e.target.value } }))} placeholder="e.g., the management team" />
          </div>
          <div className="form-group">
            <label>Restaurant Name</label>
            <input type="text" value={config.restaurantName} onChange={e => setConfig(prev => ({ ...prev, restaurantName: e.target.value }))} />
          </div>
        </div>
      </div>

      <div className="config-section">
        <h3>Escalation Contacts</h3>
        <div className="contacts-grid">
          <div className="contact-card">
            <h4>Manager</h4>
            <div className="form-group"><label>Name</label><input type="text" value={config.escalationContacts.manager.name} onChange={e => updateContact('manager', 'name', e.target.value)} /></div>
            <div className="form-group"><label>Phone</label><input type="tel" value={config.escalationContacts.manager.phone} onChange={e => updateContact('manager', 'phone', e.target.value)} /></div>
            <div className="form-group"><label>Email</label><input type="email" value={config.escalationContacts.manager.email} onChange={e => updateContact('manager', 'email', e.target.value)} /></div>
          </div>
          <div className="contact-card">
            <h4>Owner</h4>
            <div className="form-group"><label>Name</label><input type="text" value={config.escalationContacts.owner.name} onChange={e => updateContact('owner', 'name', e.target.value)} /></div>
            <div className="form-group"><label>Phone</label><input type="tel" value={config.escalationContacts.owner.phone} onChange={e => updateContact('owner', 'phone', e.target.value)} /></div>
            <div className="form-group"><label>Email</label><input type="email" value={config.escalationContacts.owner.email} onChange={e => updateContact('owner', 'email', e.target.value)} /></div>
          </div>
        </div>
      </div>

      <div className="config-actions">
        <button className="save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};
